/**
 * The project list every discipline shares: the same header, the same cards, the same
 * rows and the same way of deleting - so IAS accounting, Valuation and Baseline
 * Accounting are learned once.
 */

import { useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { AccountMenuV2 } from '../AccountMenuV2';
import type { Discipline } from '../disciplines';
import { Button } from './Button';
import { TypedConfirm } from './TypedConfirm';
import { DisciplineChip } from './DisciplineChip';

export function ProjectsPage({
  discipline,
  intro,
  onLeaveDiscipline,
  children,
}: {
  discipline: Discipline;
  intro: ReactNode;
  onLeaveDiscipline: () => void;
  children: ReactNode;
}) {
  return (
    <div data-discipline={discipline.id} className="min-h-screen bg-canvas">
      <div aria-hidden="true" className="h-1 bg-accent-600" />
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-8 sm:py-10">
        <header className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <DisciplineChip discipline={discipline} />
              <button
                type="button"
                onClick={onLeaveDiscipline}
                className="text-xs text-gray-500 underline-offset-2 hover:text-gray-800 hover:underline"
              >
                Change workspace
              </button>
            </div>
            <h1 className="mt-3 font-display text-3xl font-bold text-gray-900">
              {discipline.label} projects
            </h1>
            <p className="mt-1.5 max-w-2xl text-sm text-gray-600">{intro}</p>
          </div>
          <AccountMenuV2 />
        </header>
        {children}
      </div>
    </div>
  );
}

/** A card on the list page: starting a project, importing one. */
export function Panel({
  title,
  description,
  children,
  className,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={clsx(
        'rounded-xl border border-gray-200 bg-white p-5 shadow-[0_1px_2px_rgb(var(--c-black)/0.04)]',
        className,
      )}
    >
      <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
      {description && <p className="mt-1 text-xs leading-relaxed text-gray-600">{description}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

/** An error, with the one thing to do about it. */
export function Notice({
  tone = 'error',
  children,
  onDismiss,
}: {
  tone?: 'error' | 'warning';
  children: ReactNode;
  onDismiss?: () => void;
}) {
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={clsx(
        'mt-5 flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm',
        tone === 'error'
          ? 'border-red-200 bg-red-50 text-red-800'
          : 'border-amber-200 bg-amber-50 text-amber-900',
      )}
    >
      <div className="min-w-0">{children}</div>
      {onDismiss && (
        <button type="button" onClick={onDismiss} className="shrink-0 text-xs underline">
          Dismiss
        </button>
      )}
    </div>
  );
}

/** The list of projects. Empty and loading say what to do next. */
export function ProjectRows({
  isLoading,
  count,
  empty,
  children,
}: {
  isLoading: boolean;
  count: number;
  /** What to do when there are none - shown only then. */
  empty: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="mt-8">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Your projects</h2>
      {isLoading && count === 0 && <p className="mt-3 text-sm text-gray-500">Loading…</p>}
      {!isLoading && count === 0 && (
        <p className="mt-3 rounded-xl border border-dashed border-gray-300 bg-white/60 px-5 py-6 text-sm text-gray-600">
          {empty}
        </p>
      )}
      <ul className="mt-3 space-y-2">{children}</ul>
    </section>
  );
}

/**
 * One project. Opening is the whole row; deleting asks first, in the row - the phrase
 * "Delete Project" typed out - so the name being deleted stays on screen beside it.
 */
export function ProjectRow({
  title,
  meta,
  note,
  aside,
  folderControl,
  onOpen,
  onDelete,
}: {
  title: string;
  meta: ReactNode;
  note?: ReactNode;
  aside?: ReactNode;
  /** Moves the project between folders (ProjectFolders). */
  folderControl?: ReactNode;
  onOpen: () => void;
  /** Absent for a sample, which cannot be deleted - it is reset, or copied (ProjectFolders). */
  onDelete?: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  return (
    <li className="group flex items-center gap-4 rounded-xl border border-gray-200 bg-white px-4 py-3 transition-colors hover:border-accent-300">
      <span aria-hidden="true" className="h-9 w-1 shrink-0 rounded-full bg-accent-200 transition-colors group-hover:bg-accent-600" />
      <button type="button" onClick={onOpen} className="min-w-0 flex-1 text-left focus-visible:outline-none">
        <span className="block truncate font-display text-[0.95rem] font-semibold text-gray-900 group-hover:text-accent-700">
          {title}
        </span>
        <span className="mt-0.5 block text-xs text-gray-500">{meta}</span>
        {note && <span className="mt-1 block text-xs text-amber-700">{note}</span>}
      </button>
      {aside}
      {folderControl}
      {!onDelete ? null : confirming ? (
        <TypedConfirm
          phrase="Delete Project"
          message="This deletes the project and its history permanently."
          action="Delete permanently"
          onConfirm={() => {
            setConfirming(false);
            onDelete();
          }}
          onCancel={() => setConfirming(false)}
        />
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          title={`Delete ${title}`}
          className="shrink-0 rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-red-50 hover:text-red-700"
        >
          Delete
        </button>
      )}
    </li>
  );
}
